import * as React from 'react';

import { useQuery } from '@apollo/client';

import { isAuthenticatedUser } from 'js/lib/user';

import type { DiscoveryCollection } from 'bundles/browse/components/types/DiscoveryCollections';
import { DiscoveryBrowseCollectionsQuery } from 'bundles/enterprise-legacy-learner-home/components/single-program/SingleProgramGraphqlQueries';
import type {
  DiscoveryBrowseCollectionsQuery as DiscoveryBrowseCollectionsQueryData,
  DiscoveryBrowseCollectionsQueryVariables,
} from 'bundles/enterprise-legacy-learner-home/components/single-program/__generated__/DiscoveryBrowseCollectionsQuery';
import EnterpriseBrowseCollectionsContext from 'bundles/page-config-common/contexts/enterprise/EnterpriseBrowseCollectionsContext';
import { useProgram } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import {
  ENTERPRISE_BROWSE_COLLECTION_LIMIT,
  LOGGED_IN_ENTERPRISE_USER_CONTEXT_ID,
  LOGGED_OUT_ENTERPRISE_USER_CONTEXT_ID,
} from 'bundles/unified-home-common/constants';

type Props = {
  children: React.ReactNode;
};

const EnterpriseBrowseCollectionsProvider = ({ children }: Props) => {
  const program = useProgram();

  const programId = program?.id;
  const isAuthenticated = isAuthenticatedUser();

  const { data, loading } = useQuery<DiscoveryBrowseCollectionsQueryData, DiscoveryBrowseCollectionsQueryVariables>(
    DiscoveryBrowseCollectionsQuery,
    {
      variables: {
        contextId: isAuthenticated ? LOGGED_IN_ENTERPRISE_USER_CONTEXT_ID : LOGGED_OUT_ENTERPRISE_USER_CONTEXT_ID,
        contextType: 'PAGE',
        programId: programId || '',
        start: 0,
        limit: ENTERPRISE_BROWSE_COLLECTION_LIMIT,
      },
      skip: !programId,
      ssr: false,
      errorPolicy: 'all' as const,
      context: { clientName: 'gatewayGql' },
    }
  );

  if (!programId || loading) {
    return (
      <EnterpriseBrowseCollectionsContext.Provider
        value={{ browseCollectionsLoading: true, browseCollections: undefined }}
      >
        {children}
      </EnterpriseBrowseCollectionsContext.Provider>
    );
  }

  const browseCollections = data?.DiscoveryCollections?.queryEnterpriseCollectionsByProgram?.productCollections || [];
  // Filter out collections that contains the word "free"
  const filteredCollections = browseCollections.filter(
    (collection) => !collection?.id.includes('LmyL-2WZTmeRq-rWx2tDVw')
  );

  return (
    <EnterpriseBrowseCollectionsContext.Provider
      value={{
        browseCollectionsLoading: false,
        browseCollections: filteredCollections as DiscoveryCollection[],
      }}
    >
      {children}
    </EnterpriseBrowseCollectionsContext.Provider>
  );
};

export default EnterpriseBrowseCollectionsProvider;
